// src/encounter/lib/from-vaccination-entry.ts
// Seed the VaccinationAdminEditor from an EXISTING nested wire entry (PROD-1227 nurse "Give"/"Edit").
// Inverse of to-vaccination-entry.ts (draft → entry): maps a stored AssessmentPlanVaccination back to
//   • a Partial<VaccinationDraft> for the editor's `initial` (pre-fills whatever the doctor requested),
//   • a minimal VaccineValueSet for the editor's `vaccine` prop (the pre-encounter request carries
//     only a DDC coding — the editor needs valueCoding + route[] + unitOfMeasurement; we reconstruct
//     them from the entry so Route/Unit default sensibly without a re-fetch).
// PURE — no I/O. Dates stay no-Z ISO (the editor's toYmd narrows them to YYYY-MM-DD for the inputs).
import type { AssessmentCoding, AssessmentPlanVaccination } from '@/types/assessment-api';
import type { VaccineValueSet } from '@/shared/services/use-vaccination-search';
import type { Coding, VaccinationDraft } from '../vaccination-admin-editor';
import { keepSourceSystem } from './source-system';

/** 6-field wire coding → the editor's 3-field Coding (drops Definition/description/shortName). */
function coding3(c: AssessmentCoding | null | undefined): Coding | null {
  return c ? { code: c.code ?? '', display: c.display ?? c.Definition ?? '', system: c.system ?? '' } : null;
}

/**
 * Build the editor's `initial` from a stored entry. administratedDate/expiryDate stay null when the
 * request hasn't been administered yet (Add-mode date fields start empty — the nurse picks them;
 * never pre-populate a user-picked date, PROD-762). isOffSystemLot resets to false — a fresh
 * administration re-picks the lot via the LotPicker.
 */
export function vaccinationEntryToInitial(e: AssessmentPlanVaccination): Partial<VaccinationDraft> {
  return {
    administratedDate: e.administratedDate,
    lotNo: e.lotNo ?? '',
    isOffSystemLot: false,
    expiryDate: e.expiryDate,
    site: coding3(e.site),
    route: coding3(e.route),
    repeatPattern: coding3(e.repeatPattern),
    administrationAmount: e.administrationAmount,
    administrationUnit: e.administrationUnit?.code ?? null,
    administrationDoseQuantity: e.administrationDoseQuantity,
    administrationDoseUnit: e.administrationDoseUnit?.code ?? null,
    durationQuantity: coding3(e.durationQuantity),
    durationUnits: coding3(e.durationUnits),
    remarks: e.remarks ?? '',
    isAdministrationRequested: e.isAdministrationRequested,
  };
}

/**
 * Reconstruct a minimal VaccineValueSet for the editor's `vaccine` prop from the request's
 * coding + the existing entry. route[] seeds from the entry's stored route (so a single-route vaccine
 * stays fixed); unitOfMeasurement from the stored admin unit. `fallbackDisplay` covers a coding with
 * no display (the request title).
 *
 * The coding system is never guessed (PROD-2851, the PROD-2831 rule in `source-system.ts`): the
 * request's own system, else the saved entry's system when it carries the same code, else blank.
 * It used to default to 'DDC', which relabelled a CVX-coded vaccine (e.g. influenza, CVX 150)
 * saved without a system as DDC on its next save. A saved system is kept as it is.
 */
export function vaccinationEntryToValueSet(
  vaccine: AssessmentCoding | null | undefined,
  existing: AssessmentPlanVaccination | null | undefined,
  fallbackDisplay: string,
): VaccineValueSet {
  const route = existing?.route
    ? [{ code: existing.route.code ?? '', display: existing.route.display ?? '', system: existing.route.system ?? '' }]
    : [];
  return {
    valueCoding: {
      code: vaccine?.code ?? '',
      display: vaccine?.display ?? vaccine?.Definition ?? fallbackDisplay,
      system: keepSourceSystem({ code: vaccine?.code ?? '', system: (vaccine?.system ?? '').trim() }, existing?.vaccine).system,
    },
    unitOfMeasurement: existing?.administrationUnit?.code ?? null,
    route,
  };
}
